<style scoped lang="scss">
  input {
    display: none;
  }
  .is-dragged-over {
    border: 1px dashed #ccc;
  }
</style>

<template>
  <div @drop.prevent>
    <ValidationProvider
      ref="provider"
      :vid="name"
      :name="validateAs"
      :rules="rules"
      :bails="false"
      v-slot="{ meta, errors }"
      tag="div"
      :class="{ 'opacity-50 cursor-not-allowed pointer-events-none': disabled, 'is-focussed': isFocussed }"
    >
      <div :class="[this.class, { 'opacity-50 cursor-not-allowed pointer-events-none': disabled, 'is-focussed': isFocussed }]">
        <label v-if="label" :for="`id_${name}`" :class="labelClass" class="block mb-[4px]">
          {{ label }}
        </label>
        <label v-else :for="`id_${name}`" :class="labelClass">
          <slot></slot>
          <spinner v-if="loading && !hideSpinner" class="!text-white !h-[20px]"></spinner>
        </label>
        <input
          v-on="{ ...$listeners, change: handleFileChange, click: handleClick, focus: handleFocus, blur: handleBlur }"
          :disabled="!canUpload"
          ref="input"
          :multiple="multiple"
          type="file"
          :id="`id_${name}`"
        />
        <span v-if="showError && errors[0] && ((meta.touched && meta.validated) || meta.validated)" class="text-red text-12 block mt-4">{{ errors[0] }}</span>
      </div>
    </ValidationProvider>
  </div>
</template>

<script lang="ts">
  import { Field as ValidationProvider } from "vee-validate";
  import { Component, Vue, Prop, Emit, Watch, Ref } from "vue-facing-decorator";
  import { Action } from "vuex-facing-decorator";
  // @ts-ignore
  import { v4 as uuidv4 } from "uuid";
  import { DocumentFolder, DocumentType } from "@/store/modules/document.store";

  @Component({
    components: {
      ValidationProvider,
      Spinner: () => import("@/components/general/spinner.vue"),
    },
  })
  export default class FormFile extends Vue {
    @Action("temp-media/upload") uploadTempImage!: TempMediaUpload;
    @Action("temp-media/startUploading") startUploading!: () => void;
    @Action("temp-media/endUploading") endUploading!: () => void;
    @Action("temp-media/reset") resetMedia!: () => void;
    @Action("temp-media/setTotal") setTotal!: (payload: number) => void;
    @Action("temp-media/incrementUploaded") incrementUploaded!: () => void;
    @Action("temp-media/addError") addError!: (payload: any) => void;

    @Prop() name?: string;

    @Prop() value?: string;

    @Prop() rules?: string | object;

    @Prop() label?: string;

    @Prop() labelClass!: string;

    @Prop({ default: "" }) class!: string;

    @Prop() documentType!: DocumentType;

    @Prop() documentFolder!: DocumentFolder;

    @Prop({ default: false }) skipMutation!: boolean;

    @Prop({ default: true }) showError?: boolean;

    @Prop({ default: false }) disabled!: boolean;

    @Prop({ default: true }) canUpload!: boolean;

    @Prop({ default: false }) multiple!: boolean;

    @Prop({ default: false }) hideSpinner!: boolean;

    @Ref() input!: HTMLInputElement;

    @Ref() readonly provider!: InstanceType<typeof ValidationProvider>;

    isFocussed = false;

    localValue = "";

    loading = false;

    private get validateAs(): string {
      if (this.label) {
        return this.label;
      }

      if (this.name) {
        return this.name;
      }

      return "image field";
    }

    @Emit("change")
    async handleFileChange(e: any) {
      this.loading = true;

      let files = e.target.files || e.dataTransfer.files;

      if (!files.length) return null;

      if (!this.skipMutation) {
        this.resetMedia();

        this.startUploading();
      }

      this.setTotal(Array.from(files).length);

      let medias: any = [];
      let promises: any = [];
      Array.from(files).forEach((f: any) => {
        const payload: TempMediaUploadPayload = {
          name: f.name,
          uuid: uuidv4(),
          file: f,
          type: this.documentType,
          folder: this.documentFolder,
          skip_mutation: this.skipMutation,
        };
        promises.push(
          this.uploadTempImage(payload)
            .then((media) => {
              media.type = this.documentType;
              media.folder = this.documentFolder;
              medias.push(media);
            })
            .catch((e) => {
              this.addError(e);
            })
            .finally(() => {
              this.incrementUploaded();
            }),
        );
      });

      await Promise.all(promises);

      this.loading = false;

      if (!this.skipMutation) {
        this.endUploading();
      }

      if (!this.multiple) {
        return medias[0];
      }

      return medias;
    }

    @Emit("blur")
    private handleBlur(e: Event): Event {
      this.isFocussed = false;
      return e;
    }

    @Emit("focus")
    private handleFocus(e: Event): Event {
      this.isFocussed = true;
      return e;
    }

    @Emit("click")
    private handleClick(e: Event): Event {
      if (e.target) {
        //@ts-ignore
        e.target.value = "";
      }
      return e;
    }

    @Watch("value", { immediate: true })
    private valueChanged(newValue: string) {
      this.localValue = newValue;
    }
  }
</script>
